export interface DataBinding {
    sourceType?: 'custom_api' | 'webhook';
    customApiId?: string;
    webhookId?: string;
    apiKey: string;
    fallbackValue?: string;
}

export function resolveJsonPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    if (path === '.' || path === '') return obj;

    const cleanPath = path.replace(/\[(\d+)\]/g, '.$1');
    const parts = cleanPath.split('.').filter(Boolean);
    let current = obj;
    for (const part of parts) {
        if (current === null || current === undefined) return undefined;
        current = current[part];
    }
    return current;
}

export function resolveTemplateBindings(
    textContents: Record<string, string> = {},
    textStyles: Record<string, any> = {},
    imageSettings: Record<string, any> = {},
    scrollerSettings: Record<string, any> = {},
    customApis: any[] = [],
    showPlaceholders: boolean = false,
    webhooks: any[] = []
) {
    const resolvedTextContents = { ...textContents };
    const resolvedImageSettings = { ...imageSettings };
    const resolvedScrollerSettings = { ...scrollerSettings };

    // Helper to log or handle replacement
    const getResolvedValue = (binding: DataBinding, isText: boolean = false): string | null => {
        const isWebhook = binding.sourceType === 'webhook' || Boolean(binding.webhookId);
        const sourceId = isWebhook ? (binding.webhookId || binding.customApiId) : binding.customApiId;

        if (!sourceId || !binding.apiKey) {
            return null;
        }

        if (showPlaceholders) {
            let label = 'API';
            if (isWebhook) {
                const wh = webhooks.find((w) => String(w._id) === String(sourceId) || String(w.webhook_id) === String(sourceId));
                label = wh ? (wh.name || 'Webhook') : 'Webhook';
            } else {
                const api = customApis.find((a) => String(a._id) === String(sourceId));
                label = api ? api.name_custom : 'API';
            }
            const apiKeyPlaceholder = binding.apiKey
                .replace(/\[\d+\]/g, '[n]')
                .replace(/\.\d+\./g, '.n.')
                .replace(/\.\d+$/g, '.n')
                .replace(/^\d+\./g, 'n.');
            if (isText) {
                return `{{${label}.${apiKeyPlaceholder}}}`;
            } else {
                const textToDisplay = `${label}.${apiKeyPlaceholder}`;
                return `https://placehold.co/600x400/101928/FFFFFF?text=${encodeURIComponent(textToDisplay)}`;
            }
        }

        if (isWebhook) {
            const wh = webhooks.find((w) => String(w._id) === String(sourceId) || String(w.webhook_id) === String(sourceId));
            const dataObj = wh?.latest_payload ?? wh?.customapidata;
            if (dataObj) {
                const val = resolveJsonPath(dataObj, binding.apiKey);
                if (val !== undefined && val !== null && String(val).trim() !== '' && String(val) !== 'null' && String(val) !== 'undefined') {
                    return String(val);
                }
            }
        } else {
            const api = customApis.find((a) => String(a._id) === String(sourceId));
            if (api && api.customapidata) {
                const val = resolveJsonPath(api.customapidata, binding.apiKey);
                if (val !== undefined && val !== null && String(val).trim() !== '' && String(val) !== 'null' && String(val) !== 'undefined') {
                    return String(val);
                }
            }
        }

        if (binding.fallbackValue !== undefined && String(binding.fallbackValue).trim() !== '' && String(binding.fallbackValue) !== 'null') {
            return binding.fallbackValue;
        }
        if (isText) {
            const cleanKey = binding.apiKey.split('.').pop() || 'Data';
            const keyPlaceholder = `{{${cleanKey}}}`;
            return keyPlaceholder;
        }
        return null;
    };

    // 1. Resolve Text Contents
    Object.keys(textStyles).forEach((layerId) => {
        const style = textStyles[layerId];
        if (style?.binding) {
            const resolved = getResolvedValue(style.binding, true);
            if (resolved !== null) {
                resolvedTextContents[layerId] = resolved;
            }
        }
    });

    // 2. Resolve Image Urls
    Object.keys(imageSettings).forEach((layerId) => {
        const setting = imageSettings[layerId];
        if (setting?.binding) {
            const resolved = getResolvedValue(setting.binding);
            if (resolved !== null) {
                resolvedImageSettings[layerId] = {
                    ...setting,
                    url: resolved,
                };
            }
        }
    });

    // 3. Resolve Scroller Contents
    Object.keys(scrollerSettings).forEach((layerId) => {
        const setting = scrollerSettings[layerId];
        if (setting?.binding) {
            const resolved = getResolvedValue(setting.binding, true);
            if (resolved !== null) {
                resolvedScrollerSettings[layerId] = {
                    ...setting,
                    content: resolved,
                };
            }
        }
    });

    return {
        textContents: resolvedTextContents,
        imageSettings: resolvedImageSettings,
        scrollerSettings: resolvedScrollerSettings,
    };
}

export function findFirstArrayPath(obj: any, path: string = ''): string | null {
    if (!obj) return null;
    if (Array.isArray(obj)) return path;
    if (typeof obj === 'object') {
        const technicalKeys = new Set([
            'headers', 'config', 'request', 'response', 'status', 'statuscode',
            'success', 'message', 'error', 'errors', 'ok', 'method', 'url', 'origin',
            'headers_list', 'header_values', 'request_body', 'api_url', 'token',
            'total', 'skip', 'limit', 'count', 'size', 'page', 'pages', 'totalpages', 'perpage',
            'data_json'
        ]);
        for (const key of Object.keys(obj)) {
            if (technicalKeys.has(key.toLowerCase())) continue;
            const val = obj[key];
            const childPath = path ? `${path}.${key}` : key;
            if (Array.isArray(val)) {
                return childPath;
            }
            if (val && typeof val === 'object') {
                const nested = findFirstArrayPath(val, childPath);
                if (nested !== null) return nested;
            }
        }
    }
    return null;
}


